<script setup lang="ts">
import { onMounted, ref } from 'vue';
import FontSelector from '@/components/DesignStudio/FontSelector.vue';
import CModal from '@/components/CModal.vue';
import { useFonts, type UserFont } from '@/composables/fonts';
import type { CanvaFont } from '@/typings/layers/layers';
import type {  CSelectItem, Primitive } from '@/components/shared/CSelect';
import fontManagerService from '@/utils/fontManagerService';

type FontReplacementInput = {
    fontReplaced: string;
    userFont: UserFont;
};

const fonts = useFonts();

const props = defineProps<{
    isOpen: boolean;
    isLoading: boolean;
    notFoundFonts: CanvaFont[];
}>();

const emits = defineEmits<{
    (event: 'fonts-replaced', value: Map<string, UserFont>): void;
    (event: 'close'): void;
}>();

const fontsReplaced = ref<FontReplacementInput[]>([]);

const toCSelectItem = (font: UserFont) => ({
    id: font.Path,
    label: fonts.generateFontName(font),
    value: font.Path,
    name: font.Name,
    object: font
});

const onConfirm = () => {
    const fontsMap = new Map<string, UserFont>();
    for (const { fontReplaced, userFont } of fontsReplaced.value) {
        fontsMap.set(fontReplaced, userFont);
    }
    emits('fonts-replaced', fontsMap);
};

const onClose = () => {
    emits('close');
};

const onFontChange = (index: number, cselectItem: CSelectItem<Primitive>) => {
    fontsReplaced.value.splice(index, 1, { ...fontsReplaced.value[index], userFont: cselectItem.object as UserFont });
};

onMounted(() => {
    fontsReplaced.value =
        props.notFoundFonts.map(({ rawTypeface }) => ({
            fontReplaced: rawTypeface,
            userFont: fontManagerService.defaultFont
        })) || [];
});
</script>

<template>
    <c-modal title="Missing Fonts" size="lg" ok-title="Import Design" :value="isOpen" @close="onClose">
        <div class="fonts-not-found-warning">
            <div class="notification-message mb-1 mr-2">
                <font-awesome-icon class="mr-2" :icon="['fas', 'triangle-exclamation']" />
                We couldn’t match these fonts to your Customily library. Pick the closest match from your available
                fonts, you’ll be able to upload your own later if needed.
            </div>
        </div>
        <div class="row mt-10px">
            <div class="col-6 text-bold fs-1rem">Missing Font</div>
            <div class="col-6 text-bold fs-1rem">Replacement</div>
        </div>
        <hr />
        <div v-for="[index, font] in fontsReplaced.entries()" :key="`font-${index}`" class="row mt-10px">
            <div class="col-6 d-flex justify-content-between align-items-center">
                <span class="text-bold">{{ font.fontReplaced }}</span>
                <font-awesome-icon :icon="['fas', 'arrow-right']" size="lg" class="color-gray" />
            </div>
            <div class="col-6">
                <font-selector
                    append-to-body
                    label=""
                    :show-font-upload="false"
                    :value="toCSelectItem(font.userFont)"
                    @input="onFontChange(index, $event)"
                />
            </div>
        </div>
        <template #modal-footer>
            <div class="d-flex justify-content-end w-100">
                <b-overlay
                    :show="isLoading"
                    rounded
                    opacity="0.6"
                    spinner-small
                    spinner-variant="primary"
                    class="d-inline-block ml-2"
                >
                    <button class="btn btn-primary" :disabled="isLoading" @click="onConfirm">Import Design</button>
                </b-overlay>
            </div>
        </template>
    </c-modal>
</template>

<style lang="scss" scoped>
@import 'styles/utilities/functions';
.fonts-not-found-warning {
    display: flex;
    justify-content: space-between;
    padding: 0.625rem 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid get-color('gray-light');
    background: get-color('gray-lightest');

    .notification-message {
        font-size: 0.875rem;
        color: get-color('black');
    }
}

.fs-1rem {
    font-size: 1rem;
}

.mt-10px {
    margin-top: 0.625rem;
}

hr {
    margin-top: 0.625rem;
    margin-bottom: 0.625rem;
}
</style>
